/** Semana tipo Running (sin wearables). El coach puede editar cada día después. */

import {
  SESSION_KINDS,
  emptySportPrescription,
  RUN_TYPE_LABELS,
} from './trainingSportSession';
import { normalizeWeekday, sequentialWeekdays } from './trainingWeekday';

export const RUN_GOALS = Object.freeze([
  { id: '5k', label: '5 km' },
  { id: '10k', label: '10 km' },
  { id: '21k', label: 'Media maratón (21 km)' },
  { id: '42k', label: 'Maratón (42 km)' },
  { id: 'base', label: 'Base / mantenimiento' },
]);

export const RUN_LEVELS = Object.freeze([
  { id: 'principiante', label: 'Principiante' },
  { id: 'intermedio', label: 'Intermedio' },
  { id: 'avanzado', label: 'Avanzado' },
]);

const WEEK_LAYOUTS = Object.freeze({
  1: ['easy'],
  2: ['easy', 'long'],
  3: ['easy', 'intervals', 'long'],
  4: ['easy', 'intervals', 'recovery', 'long'],
  5: ['easy', 'intervals', 'recovery', 'tempo', 'long'],
  6: ['easy', 'intervals', 'easy', 'tempo', 'recovery', 'long'],
  7: ['easy', 'intervals', 'easy', 'tempo', 'recovery', 'easy', 'long'],
});

const BASE_KM = Object.freeze({
  '5k': { easy: 4, long: 7, intervals: 5, tempo: 5, recovery: 3 },
  '10k': { easy: 6, long: 10, intervals: 7, tempo: 7, recovery: 4 },
  '21k': { easy: 8, long: 14, intervals: 8, tempo: 9, recovery: 5 },
  '42k': { easy: 10, long: 18, intervals: 10, tempo: 12, recovery: 6 },
  base: { easy: 5, long: 8, intervals: 6, tempo: 6, recovery: 4 },
});

const LEVEL_MULT = Object.freeze({
  principiante: 0.85,
  intermedio: 1,
  avanzado: 1.15,
});

const ZONE_BY_TYPE = Object.freeze({
  easy: 2,
  recovery: 1,
  long: 2,
  tempo: 3,
  intervals: 4,
});

const PACE_OFFSET_SEC = Object.freeze({
  easy: 0,
  recovery: 20,
  long: 10,
  tempo: -25,
  intervals: -40,
});

function roundKm(n) {
  return Math.round(n * 10) / 10;
}

function clampPace(sec) {
  return Math.max(150, Math.min(720, Math.round(sec)));
}

export function intervalsNoteFor({ goal, level }) {
  const g = String(goal || '10k');
  const lv = String(level || 'intermedio');
  if (g === '5k') {
    return lv === 'avanzado' ? '8×400 m, recuperar 75 s trote' : '6×400 m, recuperar 90 s trote';
  }
  if (g === '10k') {
    return lv === 'principiante' ? '5×400 m, recuperar 90 s' : '5×800 m, recuperar 90 s trote';
  }
  if (g === '21k') return '4×1000 m, recuperar 2 min trote';
  if (g === '42k') return '6×800 m, recuperar 90 s trote';
  return '8×200 m, recuperar 60 s trote';
}

export function coachNotesForRunType(runType, { goal, level } = {}) {
  const label = RUN_TYPE_LABELS[runType] || runType;
  if (runType === 'intervals') {
    return `${label}. ${intervalsNoteFor({ goal, level })}. Calienta 10–15 min fácil antes de las series.`;
  }
  if (runType === 'tempo') {
    return `${label}. Ritmo controlado, conversación corta. No es un 5k a tope.`;
  }
  if (runType === 'long') {
    return `${label}. Distancia de la semana: acaba pudiendo hablar. Hidratación si supera 60 min.`;
  }
  if (runType === 'recovery') {
    return `${label}. Muy suave. Si no hay piernas, camina/trote mixto.`;
  }
  return `${label}. Ritmo conversacional (zona 2). Video de técnica opcional del coach si lo deja en el día.`;
}

export function generateRunningWeekPlan({
  goal = '10k',
  level = 'intermedio',
  daysPerWeek = 4,
  easyPaceSecPerKm = 330,
  surface = 'asphalt',
  weekdays,
} = {}) {
  const g = BASE_KM[goal] ? goal : '10k';
  const lv = LEVEL_MULT[level] ? level : 'intermedio';
  const n = Math.min(7, Math.max(1, Number(daysPerWeek) || 4));
  const layout = WEEK_LAYOUTS[n] || WEEK_LAYOUTS[4];
  const easy = Number(easyPaceSecPerKm) > 0 ? Number(easyPaceSecPerKm) : 330;
  const mult = LEVEL_MULT[lv];
  const daysOfWeek = Array.isArray(weekdays) && weekdays.length
    ? weekdays.map((w) => normalizeWeekday(w)).map((w, i) => w || sequentialWeekdays(n)[i])
    : sequentialWeekdays(n);

  return layout.map((runType, i) => {
    const km = roundKm((BASE_KM[g][runType] || 5) * mult);
    const pace = clampPace(easy + (PACE_OFFSET_SEC[runType] || 0));
    const rx = {
      ...emptySportPrescription(SESSION_KINDS.RUNNING),
      surface,
      run_type: runType,
      zone_target: ZONE_BY_TYPE[runType] || 2,
      pace_target_sec_per_km: pace,
      distance_target_km: km,
      include_brick: false,
      transition_bike_run_pace_sec: null,
      intervals_note: runType === 'intervals' ? intervalsNoteFor({ goal: g, level: lv }) : '',
    };
    return {
      dia: i + 1,
      nombre: `Día ${i + 1} · ${RUN_TYPE_LABELS[runType]}`,
      weekday: daysOfWeek[i] || sequentialWeekdays(n)[i],
      completado: false,
      session_kind: SESSION_KINDS.RUNNING,
      sport_prescription: rx,
      warmup_url: '',
      stretching_url: '',
      technique_url: '',
      coach_notes: coachNotesForRunType(runType, { goal: g, level: lv }),
      cardio: { goal: 'oxidación', bpm: 130, limit_type: 'time', limit_value: 20 },
      ejercicios: [],
    };
  });
}

/** Añade días Running/Natación al plan; no borra ni pisa gym u otros deportes. */
export function appendGeneratedDays(existing = [], generated = []) {
  const base = Array.isArray(existing) ? existing.slice() : [];
  const extra = Array.isArray(generated) ? generated : [];
  const start = base.length;
  return [
    ...base,
    ...extra.map((d, i) => ({
      ...d,
      dia: start + i + 1,
    })),
  ];
}

/** Aplica km y notas del coach que el entrenador editó en el wizard. */
export function applyRunningRowEdits(days = [], edits = {}) {
  return (Array.isArray(days) ? days : []).map((d, i) => {
    const e = edits[i] || edits[String(i)] || {};
    let km = Number(d.sport_prescription?.distance_target_km);
    if (e.km != null && e.km !== '') {
      const n = Number(e.km);
      if (Number.isFinite(n) && n > 0) km = Math.round(n * 10) / 10;
    }
    const notes = e.notes != null ? String(e.notes).slice(0, 4000) : d.coach_notes;
    return {
      ...d,
      coach_notes: notes,
      sport_prescription: {
        ...(d.sport_prescription || {}),
        distance_target_km: Number.isFinite(km) ? km : d.sport_prescription?.distance_target_km,
      },
    };
  });
}
